import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_SOCIAL_LINKS, normalizeSocialLinks } from '@/lib/socialLinks';
import { deepMerge, sanitizeContent } from '@/lib/contentMerge';
import {
  DEFAULT_LOCALE,
  STORAGE_KEY,
  detectLocale,
  formatCopyright,
  isLocale,
  translations,
} from './index';

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(DEFAULT_LOCALE);
  const [contentOverrides, setContentOverrides] = useState({});
  const [socialLinks, setSocialLinks] = useState(DEFAULT_SOCIAL_LINKS);

  useEffect(() => {
    setLocaleState(detectLocale());
  }, []);

  useEffect(() => {
    function fetchRemoteContent() {
      fetch('/api/content', { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : {}))
        .then((data) => {
          if (data && typeof data === 'object') setContentOverrides(data);
        })
        .catch(() => {});

      fetch('/api/settings', { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : {}))
        .then((data) => {
          if (Array.isArray(data?.socialLinks)) {
            setSocialLinks(normalizeSocialLinks(data.socialLinks));
          }
        })
        .catch(() => {});
    }

    fetchRemoteContent();

    function handleVisibility() {
      if (!document.hidden) fetchRemoteContent();
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    localStorage.setItem(STORAGE_KEY, locale);
  }, [locale]);

  const setLocale = (next) => {
    if (isLocale(next)) setLocaleState(next);
  };

  const value = useMemo(() => {
    const staticContent = translations[locale] ?? translations[DEFAULT_LOCALE];
    const override = contentOverrides[locale];
    const hasValidOverride =
      override !== null &&
      override !== undefined &&
      typeof override === 'object' &&
      !Array.isArray(override);
    const merged = hasValidOverride ? deepMerge(staticContent, override) : staticContent;
    const canonicalRoutes = contentOverrides.es?.activeRoutes?.routes;
    const content = sanitizeContent(merged, canonicalRoutes);

    return {
      locale,
      setLocale,
      content,
      socialLinks,
      copyright: formatCopyright(
        content.footer?.copyright ?? translations[DEFAULT_LOCALE].footer.copyright,
        new Date().getFullYear(),
      ),
    };
  }, [locale, contentOverrides, socialLinks]);

  useEffect(() => {
    document.title = `${value.content.brand.name} — ${value.content.brand.tagline}`;
  }, [value.content.brand.name, value.content.brand.tagline]);

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
